import { useCallback, useRef, useState } from 'react';
import { useApiClient } from 'src/hooks/useApiClient';
import { runPool } from '../common/utils';
import type { PlatformVehicleImportExcelRequest } from '../types';
import type { ImportOutcome } from './importOutcome';

export interface ExcelImportItem {
	rowId: string;
	body: PlatformVehicleImportExcelRequest;
}

const CONCURRENCY = 6;

const errorMessage = (reason: unknown): string =>
	(reason as { message?: string })?.message || 'Import failed';

export const useExcelImport = () => {
	const { apiClient } = useApiClient();
	const [outcomes, setOutcomes] = useState<Record<string, ImportOutcome>>({});
	const [isRunning, setIsRunning] = useState(false);
	const runningRef = useRef(false);

	const setOutcome = useCallback((rowId: string, outcome: ImportOutcome) => {
		setOutcomes((prev) => ({ ...prev, [rowId]: outcome }));
	}, []);

	const run = useCallback(
		async (items: ExcelImportItem[]) => {
			if (runningRef.current || items.length === 0) return;
			runningRef.current = true;
			setIsRunning(true);

			setOutcomes((prev) => {
				const next = { ...prev };
				items.forEach((item) => {
					next[item.rowId] = { status: 'queued' };
				});
				return next;
			});

			await runPool(
				items,
				async (item) => {
					setOutcome(item.rowId, { status: 'importing' });
					const res =
						await apiClient.motornetControllerService.importVehicleExcel(item.body);
					if (res?.success === false) {
						throw new Error(res.error || 'Import failed');
					}
					return res;
				},
				{
					concurrency: CONCURRENCY,
					onSettle: (item, result) => {
						setOutcome(
							item.rowId,
							result.status === 'fulfilled'
								? { status: 'success' }
								: { status: 'error', error: errorMessage(result.reason) }
						);
					},
				}
			);

			runningRef.current = false;
			setIsRunning(false);
		},
		[apiClient, setOutcome]
	);

	const reset = useCallback(() => setOutcomes({}), []);

	return { outcomes, run, reset, isRunning };
};
